import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { setSelectedTemplate } from '../../../features/templates/templatesSlice';

// Inline SVG paths to avoid import issues
const svgPaths = {
  // Common
  p5830500: "M9 12.75C9.41421 12.75 9.75 12.4142 9.75 12C9.75 11.5858 9.41421 11.25 9 11.25C8.58579 11.25 8.25 11.5858 8.25 12C8.25 12.4142 8.58579 12.75 9 12.75Z",
  pa1d3d00: "M14.25 7.5H3.75C2.92157 7.5 2.25 8.17157 2.25 9V15C2.25 15.8284 2.92157 16.5 3.75 16.5H14.25C15.0784 16.5 15.75 15.8284 15.75 15V9C15.75 8.17157 15.0784 7.5 14.25 7.5Z",

  // Locked Shackle
  p3afaea00: "M5.25 7.5V5.25C5.25 4.25544 5.64509 3.30161 6.34835 2.59835C7.05161 1.89509 8.00544 1.5 9 1.5C9.99456 1.5 10.9484 1.89509 11.6517 2.59835C12.3549 3.30161 12.75 4.25544 12.75 5.25V7.5",

  // Unlocked Shackle
  p26845360: "M5.25 7.5V5.25C5.25004 4.42453 5.52244 3.62213 6.02497 2.96725C6.5275 2.31237 7.23207 1.8416 8.02942 1.62795C8.82676 1.4143 9.67232 1.4697 10.435 1.78558C11.1976 2.10145 11.8347 2.66014 12.2475 3.375",
};

type Wrapper2Props = {
  additionalClassNames?: string;
};

function Wrapper2({ children, additionalClassNames = "" }: React.PropsWithChildren<Wrapper2Props>) {
  return (
    <div className={additionalClassNames}>
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
        {children}
      </svg>
    </div>
  );
}

interface Frame1Props {
  children: React.ReactNode;
  onClick?: () => void;
  isActive?: boolean;
}

function Frame1({ children, onClick, isActive = false }: Frame1Props) {
  const activeColor = "#2c9944";
  const defaultColor = "var(--stroke-0, #475367)";

  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child) && child.type === 'g') {
      const groupChildren = React.Children.map(child.props.children, grandChild => {
        if (React.isValidElement(grandChild)) {
          return React.cloneElement(grandChild as React.ReactElement<any>, {
            stroke: isActive ? activeColor : defaultColor
          });
        }
        return grandChild;
      });
      return React.cloneElement(child, {}, groupChildren);
    }
    return child;
  });

  return (
    <div
      className={`relative shrink-0 size-[20px] transition-all duration-200 ${onClick ? 'cursor-pointer hover:opacity-80' : ''}`}
      onClick={onClick}
    >
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 18 18">
        {childrenWithProps}
      </svg>
    </div>
  );
}

type Text1Props = {
  text: string;
  value: string;
  onChange: (val: string) => void;
  error?: boolean;
};

function Text1({ text, value, onChange, error }: Text1Props) {
  return (
    <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-[132px]">
      <div className="flex flex-col font-sans font-normal justify-center leading-[0] relative shrink-0 text-[#101928] text-[13px] w-full" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        <p className={`leading-[normal] ${error ? 'text-red-500 font-bold' : ''}`}>{text}</p>
      </div>
      <div className="h-[36px] relative rounded-[4px] shrink-0 w-full group transition-all">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`peer absolute inset-0 w-full h-full rounded-[4px] px-3 text-[13px] outline-none bg-transparent z-10 ${error ? 'text-red-600' : 'text-[#1d2739]'} placeholder:text-[#667185]`}
        />
        <div 
          aria-hidden="true" 
          className={`absolute border border-solid inset-0 pointer-events-none rounded-[4px] transition-all duration-200 
            ${error 
              ? 'border-red-400 bg-red-50/10 peer-focus:border-red-500 peer-focus:shadow-[0_0_0_4px_rgba(239,68,68,0.1)]' 
              : 'border-[#e4e7ec] peer-focus:border-[#59b96f] peer-focus:shadow-[0_0_0_4px_rgba(89,185,111,0.1)]'
            }`} 
        />
      </div>
    </div>
  );
}


interface CustomSizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply?: (width: number, height: number, unit: string) => void;
  initialDimensions?: { width: number, height: number, unit: string };
}

export const CustomSizeModal = ({ isOpen, onClose, onApply, initialDimensions }: CustomSizeModalProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [width, setWidth] = React.useState<string>(initialDimensions?.width?.toString() || '1920');
  const [height, setHeight] = React.useState<string>(initialDimensions?.height?.toString() || '1080');
  const [isLocked, setIsLocked] = React.useState<boolean>(false);
  const [lockedRatio, setLockedRatio] = React.useState<number | null>(null);
  const unit = 'px'; // Only px unit is enough

  const handleCreate = () => {
    let w = parseFloat(width);
    let h = parseFloat(height);

    const isValid = !isNaN(w) && !isNaN(h) && w >= 300 && w <= 1960 && h >= 300 && h <= 1960;
    if (!isValid) return;

    if (onApply) {
      onApply(w, h, unit);
      onClose();
      return;
    }

    // Clear local storage for fresh start
    localStorage.removeItem('figma-make-template-state');

    dispatch(setSelectedTemplate({
      id: `custom-${Date.now()}`,
      name: `Custom ${w}x${h}${unit}`,
      previewUrl: '',
      width: w,
      height: h,
      isCustom: false
    }));

    navigate('/template-editor');
    onClose();
  };

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Reset state on open with initialDimensions or defaults
      setWidth(initialDimensions?.width?.toString() || '1920');
      setHeight(initialDimensions?.height?.toString() || '1080');
      setIsLocked(false);
      setLockedRatio(null);
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  // Clean up overflow on unmount
  React.useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const toggleLock = () => {
    if (isLocked) {
      setIsLocked(false);
      setLockedRatio(null);
    } else {
      const w = parseFloat(width);
      const h = parseFloat(height);
      if (!isNaN(w) && !isNaN(h) && h !== 0) {
        setLockedRatio(w / h);
        setIsLocked(true);
      } else {
        setLockedRatio(1920 / 1080); // Default ratio
        setIsLocked(true);
      }
    }
  };

  const handleWidthChange = (val: string) => {
    setWidth(val);
    if (isLocked && lockedRatio !== null) {
      const w = parseFloat(val);
      if (!isNaN(w)) {
        setHeight(Math.round(w / lockedRatio).toString());
      } else {
        setHeight('');
      }
    }
  };

  const handleHeightChange = (val: string) => {
    setHeight(val);
    if (isLocked && lockedRatio !== null) {
      const h = parseFloat(val);
      if (!isNaN(h)) {
        setWidth(Math.round(h * lockedRatio).toString());
      } else {
        setWidth('');
      }
    }
  };

  const calculatePreviewStyle = () => {
    // The inner container is w-full of the parent which is w-[540px] minus padding etc?
    // In design: inner gradient div is w-[518px] h-[197px] max inside a h-[213px] container
    const maxWidth = 518;
    const maxHeight = 197;

    const w = parseFloat(width);
    const h = parseFloat(height);

    if (!w || !h || isNaN(w) || isNaN(h)) {
      return { width: '518px', height: '197px' };
    }

    const aspect = w / h;
    const containerAspect = maxWidth / maxHeight;

    let newW, newH;

    if (aspect > containerAspect) {
      newW = maxWidth;
      newH = maxWidth / aspect;
    } else {
      newH = maxHeight;
      newW = maxHeight * aspect;
    }

    return {
      width: `${newW}px`,
      height: `${newH}px`,
    };
  };

  const previewStyle = calculatePreviewStyle();

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-[rgba(16,25,40,0.5)] backdrop-blur-[2.5px]"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />

          {/* Modal Content */}
          <motion.div
            className="relative bg-white h-auto overflow-hidden rounded-[12px] w-[576px] shadow-2xl"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.3, type: "spring", stiffness: 300, damping: 25 }}
          >
            <div className="flex flex-col gap-[24px] items-start p-[24px] px-[18px] w-full">
              {/* Header */}
              <div className="content-stretch flex items-center justify-between relative shrink-0 w-full">
                <div className="flex flex-col font-sans font-bold h-full justify-center leading-[0] relative shrink-0 text-[#101928] text-[20px] w-auto" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                  <p className="leading-[normal]">Create Custom Size</p>
                </div>
                <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={onClose}>
                  <Wrapper2 additionalClassNames="relative shrink-0 size-[24px]">
                    <g id="Frame">
                      <path d="M18 6L6 18" id="Vector" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                      <path d="M6 6L18 18" id="Vector_2" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                    </g>
                  </Wrapper2>
                </div>
              </div>

              {/* Body */}
              <div className="content-stretch flex flex-col gap-[24px] items-start justify-center relative shrink-0 w-full">
                {/* Controls */}
                <div className="content-stretch flex flex-col items-start relative shrink-0 w-full">
                  <div className="content-stretch flex gap-[14px] items-end relative shrink-0">
                    <Text1 
                      text="Width" 
                      value={width} 
                      onChange={handleWidthChange} 
                      error={width !== '' && (parseFloat(width) < 300 || parseFloat(width) > 1960)}
                    />
                    <Frame1 onClick={toggleLock} isActive={isLocked}>
                      {isLocked ? (
                        <g id="Locked">
                          <path d={svgPaths.p5830500} id="Vector" stroke="var(--stroke-0, #475367)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                          <path d={svgPaths.pa1d3d00} id="Vector_2" stroke="var(--stroke-0, #475367)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                          <path d={svgPaths.p3afaea00} id="Vector_3" stroke="var(--stroke-0, #475367)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                        </g>
                      ) : (
                        <g id="Unlocked">
                          <path d={svgPaths.p5830500} id="Vector" stroke="var(--stroke-0, #475367)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                          <path d={svgPaths.pa1d3d00} id="Vector_2" stroke="var(--stroke-0, #475367)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                          <path d={svgPaths.p26845360} id="Vector_3" stroke="var(--stroke-0, #475367)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                        </g>
                      )}
                    </Frame1>
                    <Text1 
                      text="Height" 
                      value={height} 
                      onChange={handleHeightChange} 
                      error={height !== '' && (parseFloat(height) < 300 || parseFloat(height) > 1960)}
                    />

                    {/* Unit Selector removed as per user request (px only) */}
                    <div className="flex items-center px-3 py-2 bg-gray-50 rounded border border-gray-100 min-w-[50px] justify-center">
                      <span className="text-[13px] font-bold text-gray-500 uppercase">px</span>
                    </div>
                  </div>
                  
                  {/* Validation Message */}
                  {((width && (parseFloat(width) < 300 || parseFloat(width) > 1960)) || 
                    (height && (parseFloat(height) < 300 || parseFloat(height) > 1960))) && (
                    <div className="mt-2 text-[11px] text-red-500 font-medium animate-in fade-in slide-in-from-top-1">
                      ⚠️ Dimensions must be between 300px and 1960px
                    </div>
                  )}
                </div>

                {/* Preview */}
                <div className="h-[213px] relative rounded-[6px] shrink-0 w-full flex items-center justify-center bg-gray-50/50">
                  <div className="overflow-hidden relative rounded-[inherit] size-full flex items-center justify-center p-6">
                    <div
                      className="bg-white border border-gray-100 shadow-[0_12px_24px_rgba(0,0,0,0.06)] transition-all duration-300 ease-out"
                      style={{
                        width: previewStyle.width,
                        height: previewStyle.height
                      }}
                    >
                       <div className="w-full h-full bg-gradient-to-br from-[#0ba29b]/10 to-[#43b48d]/10 flex items-center justify-center">
                         <span className="text-[10px] font-bold text-[#0ba29b]/30">PREVIEW</span>
                       </div>
                    </div>
                  </div>
                  <div aria-hidden="true" className="absolute border border-[#f0f2f5] border-solid inset-0 pointer-events-none rounded-[6px]" />
                </div>
              </div>

              {/* Footer / Create Button */}
              <div className="flex justify-end w-full">
                {(() => {
                  const w = parseFloat(width);
                  const h = parseFloat(height);
                  const isValid = !isNaN(w) && !isNaN(h) && w >= 300 && w <= 1960 && h >= 300 && h <= 1960;
                  
                  return (
                    <div
                      className={`content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] w-[114px] transition-all ${isValid ? 'bg-[#2c9944] cursor-pointer hover:brightness-95' : 'bg-gray-300 cursor-not-allowed opacity-50'}`}
                      data-name="Primary - Sm"
                      onClick={() => isValid && handleCreate()}
                    >
                      <p className="font-sans font-medium leading-[normal] relative shrink-0 text-[14px] text-nowrap text-white" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                        {onApply ? 'Apply Changes' : 'Create'}
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

